<template>
  <v-row justify="center">
    <v-dialog
      v-model="showDialog"
      persistent
      max-width="600px"
    >
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ action === 'Save' ? 'Add' : 'Update' }} Concentrator</span>
        </v-card-title>
        <v-form
          ref="form"
          method="post"
          action="/"
          lazy-validation
          @submit.prevent="saveOrUpdate()"
        >
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="form.name"
                    label="Concentrator name"
                    placeholder="Some cool name"
                    :rules="[
                      v => !!v || 'Concentrator name is required',
                    ]"
                    required
                  ></v-text-field>
                  <small
                    v-show="form.errors.has('name')"
                    class="validation-error"
                  >
                    {{ form.errors.get('name') }}
                  </small>
                  <v-text-field
                    v-model="form.concentrator_id"
                    label="Concentrator ID"
                    placeholder="Unique identifier"
                    :rules="[
                      v => !!v || 'Concentrator ID is required',
                    ]"
                    :disabled="action === 'Update'"
                    required
                  ></v-text-field>
                  <small
                    v-show="form.errors.has('concentrator_id')"
                    class="validation-error"
                  >
                    {{ form.errors.get('concentrator_id') }}
                  </small>
                  <v-select
                    v-model="form.service_provider"
                    :items="providerOptions"
                    label="DCU service provider"
                    :rules="[v => !!v || 'Service provider is required']"
                  />
                  <small v-show="form.errors.has('service_provider')" class="validation-error">
                    {{ form.errors.get('service_provider') }}
                  </small>
                  <p v-if="action === 'Update'">
                    Active: {{ concentrator.service_provider }}<span v-if="concentrator.requested_service_provider">; requested: {{ concentrator.requested_service_provider }}</span>
                  </p>
                </v-col>
              </v-row>
            </v-container>
            <v-alert v-if="requestError" type="error" outlined dense>
              <div style="max-height: 240px; overflow-y: auto; overflow-wrap: anywhere; white-space: pre-line;">{{ requestErrorDetails }}</div>
            </v-alert>
            <v-alert v-if="provisioning" type="info" outlined dense>
              <div>Active: {{ provisioning.active_provider }}; requested: {{ provisioning.requested_provider }}</div>
              <div>Stage: {{ provisioning.data.stage }} ({{ provisioning.data.status }})</div>
              <div>Registered: {{ provisioning.data.registered_meter_count }} / {{ provisioning.data.meter_count }}; command ready: {{ provisioning.data.command_ready_meter_count }} / {{ provisioning.data.meter_count }}</div>
              <div v-if="provisioning.operator_action">{{ provisioning.operator_action }}</div>
              <div v-if="provisioning.data.last_error">{{ provisioning.data.last_error }}</div>
              <div v-for="item in failedItems" :key="item.id">{{ item.meter_serial }}: {{ item.last_error }}</div>
              <v-btn v-if="can('concentrator-edit') && ['failed', 'waiting'].includes(provisioning.data.status)" small text :loading="retrying" @click="retryProvisioning">Retry preparation</v-btn>
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="blue darken-1"
              text
              @click="closeDialog()"
            >
              Close
            </v-btn>
            <v-btn
              color="blue darken-1"
              text
              type="submit"
              :loading="form.busy"
            >
              {{ action }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import Form from 'vform'
import axios from 'axios'
import hasPermission from '@/mixins/hasPermission'

export default {
  mixins: [hasPermission],
  props: {
    showDialog: {
      type: Boolean,
    },
    concentrator: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    form: new Form({
      name: '',
      concentrator_id: '',
      service_provider: 'hexing',
      id: '', // Assuming you'll have an 'id' field for updates
    }),
    action: 'Save',
    provisioning: null,
    pollTimer: null,
    retrying: false,
    requestError: '',
  }),
  computed: {
    requestErrorDetails() {
      return this.requestError.replace('Reconcile these records before retrying: ', 'Reconcile these records before retrying:\n').split('; ').join('\n')
    },
    failedItems() {
      return (this.provisioning?.data?.items || []).filter(item => item.last_error)
    },
    providerOptions() {
      const options = [{ text: 'Hexing', value: 'hexing' }, { text: 'Progressive', value: 'progressive' }]
      if (this.concentrator && this.concentrator.service_provider === 'legacy') options.unshift({ text: 'Legacy', value: 'legacy' })
      return options
    },
  },
  watch: {
    showDialog: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) this.initializeForm()
      },
    },
  },
  beforeDestroy() {
    clearTimeout(this.pollTimer)
  },
  methods: {
    initializeForm() {
      this.requestError = ''
      this.form.clear()
      if (this.concentrator && Object.keys(this.concentrator).length > 0) {
        this.form.update(this.concentrator)
        this.form.service_provider = this.concentrator.requested_service_provider || this.concentrator.service_provider || 'hexing'
        this.action = 'Update'
        if (this.showDialog && this.can('concentrator-edit')) this.loadProvisioning()
      } else {
        this.form.update({
          name: '', concentrator_id: '', service_provider: 'hexing', id: '',
        })
        this.action = 'Save'
      }
    },
    showRequestError(error, fallback) {
      const message = error.response?.data?.message
      this.requestError = typeof message === 'string' && message.trim() ? message : fallback
      const summary = this.requestError.startsWith('Unresolved Hexing token or valve commands block activation.')
        ? 'Activation blocked by unresolved Hexing commands. See details below.'
        : this.requestError
      this.$notification.error(summary.length > 180 ? `${fallback}. See details below.` : summary)
    },
    loadProvisioning() {
      clearTimeout(this.pollTimer)
      if (!this.form.id || !this.showDialog || !this.can('concentrator-edit')) return
      const id = this.form.id
      axios.get(`concentrators/${id}/progressive-provisioning`).then(response => {
        if (this.form.id !== id || !this.showDialog) return
        this.provisioning = response.data
        if (this.provisioning && !['completed', 'cancelled'].includes(this.provisioning.data.status)) {
          this.pollTimer = setTimeout(() => this.loadProvisioning(), 10000)
        }
      }).catch(() => {
        if (this.form.id === id && this.showDialog) this.pollTimer = setTimeout(() => this.loadProvisioning(), 30000)
      })
    },
    retryProvisioning() {
      this.requestError = ''
      this.retrying = true
      axios.post(`concentrators/${this.form.id}/progressive-provisioning/${this.provisioning.data.id}/retry`)
        .then(() => this.loadProvisioning())
        .catch(error => this.showRequestError(error, 'Unable to retry preparation'))
        .finally(() => { this.retrying = false })
    },
    closeDialog() {
      this.requestError = ''
      clearTimeout(this.pollTimer)
      this.provisioning = null
      this.$emit('close')
      this.$refs.form.resetValidation()
      this.form.reset()
      this.form.service_provider = 'hexing'
    },
    saveOrUpdate() {
      this.requestError = ''
      const isFormValid = this.$refs.form.validate()
      if (isFormValid) {
        this.action === 'Save' ? this.saveConcentrator() : this.updateConcentrator()
      }
    },
    saveConcentrator() {
      this.form.post('concentrators')
        .then(response => {
          if (response.data.provisioning && this.can('concentrator-edit')) {
            this.form.update(response.data.message)
            this.action = 'Update'
            this.provisioning = response.data.provisioning
            this.$emit('concentrator-added')
            this.loadProvisioning()
            return
          }
          this.$notification.success('Concentrator added successfully')
          this.closeDialog()
          this.$emit('concentrator-added')
        })
        .catch(error => {
          this.showRequestError(error, 'Unable to add concentrator')
        })
    },
    updateConcentrator() {
      this.form.put(`concentrators/${this.form.id}`)
        .then(response => {
          if (response.data.provisioning) {
            this.provisioning = response.data.provisioning
            this.$emit('concentrator-added')
            this.loadProvisioning()
            return
          }
          this.$notification.success('Concentrator updated successfully')
          this.closeDialog()
          this.$emit('concentrator-added')
        })
        .catch(error => {
          this.showRequestError(error, 'Unable to update concentrator')
        })
    },
  },
}
</script>
